import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, useScroll, useSpring } from 'framer-motion';
import { ArrowLeftIcon } from 'lucide-react';
import { Logo } from '../Logo';
import { ThemeToggle } from '../ThemeToggle';
import { PasswordModal } from './PasswordModal';
import { useCaseStudyAccess } from '../../contexts/CaseStudyAccessContext';
import { profile } from '../../data/profile';
import { workItems } from '../../data/work';
type CaseStudyShellProps = {
  children: React.ReactNode;
};
export function CaseStudyShell({
  children
}: CaseStudyShellProps) {
  const {
    scrollYProgress
  } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 220,
    damping: 40,
    restDelta: 0.001
  });
  const {
    pathname
  } = useLocation();
  const navigate = useNavigate();
  const {
    isUnlocked
  } = useCaseStudyAccess();

  // Normalize the path (trailing slashes, hosting prefixes) before matching, and only lock pages explicitly marked locked.
  const cleanPath = pathname.replace(/\/+$/, '');
  // Sub-pages (e.g. /work/clear-docs/review) inherit their parent case study's lock.
  const item = workItems.find(w => w.href && (cleanPath.endsWith(w.href) || cleanPath.includes(`${w.href}/`)));
  const isLocked = Boolean(item?.locked) && !isUnlocked;
  return <div className="min-h-screen w-full bg-canvas text-fg" data-id="element-51" data-mp="1d6mqoucl60bd">
      {!isLocked && <motion.div aria-hidden className="fixed inset-x-0 top-0 z-50 h-0.5 origin-left bg-coral" style={{
      scaleX
    }} data-id="element-52" data-mp="20xa8c7cb04b4" />}
      <header className="sticky top-0 z-40 bg-canvas/90 backdrop-blur" data-id="element-53" data-mp="25v23da88w6jj">
        <div className="mx-auto flex max-w-[920px] items-center justify-between px-5 py-4 md:px-8" data-id="element-54" data-mp="1r7qs79fz6ojy">
          <Link to={{
          pathname: '/',
          hash: '#work'
        }} className="group inline-flex items-center gap-1.5 text-sm font-medium text-fg-muted transition-colors duration-150 hover:text-ink" data-id="element-55" data-mp="1w5s9ppxr6ftb">
            <ArrowLeftIcon className="h-4 w-4 transition-transform duration-200 ease-out group-hover:-translate-x-0.5" data-id="element-56" data-mp="2onmqsc56djfy" />
            All case studies
          </Link>
          <div className="flex items-center gap-3" data-id="element-57" data-mp="2tjqj425otjrt">
            <ThemeToggle data-id="element-58" data-mp="2tk093pqqfqxi" />
            <Link to="/" className="flex items-center gap-2" data-id="element-59" data-mp="2thnw7qzu06rb">
              <Logo className="h-8 w-8" data-id="element-60" data-mp="2pgkiba1cgksq" />
              <span className="hidden text-sm font-semibold text-fg sm:inline" data-id="element-61" data-mp="2kisoubl1wqmf">{profile.name}</span>
            </Link>
          </div>
        </div>
      </header>

      {isLocked ? <>
          <main aria-hidden className="pointer-events-none mx-auto max-w-[920px] select-none px-5 pb-16 pt-8 blur-md md:px-8 md:pt-12" data-id="element-62" data-mp="2flk17vh4vuds">
            <div className="h-3 w-56 rounded-full bg-coral-soft" data-id="element-63" data-mp="25u5yletvyegf" />
            <p className="mt-5 max-w-3xl text-[34px] font-semibold leading-[1.1] tracking-tight text-ink md:text-5xl" data-id="element-64" data-mp="21t3zcxzyy2eg">
              {item?.title ?? 'Case study'}
            </p>
            <div className="mt-6 max-w-2xl space-y-2.5" data-id="element-65" data-mp="1ruelrhf3v440">
              <div className="h-3 w-full rounded-full bg-line" data-id="element-66" data-mp="1ruwrd4nnquol" />
              <div className="h-3 w-11/12 rounded-full bg-line" data-id="element-67" data-mp="7b1691diqjf0" />
              <div className="h-3 w-3/4 rounded-full bg-line" data-id="element-68" data-mp="cax9uzib08u1" />
            </div>
            <div className="mt-14 aspect-[16/8] rounded-2xl bg-surface" data-id="element-69" data-mp="cd098a5cjebf" />
          </main>
          <PasswordModal open onClose={() => navigate({
        pathname: '/',
        hash: '#work'
      })} onSuccess={() => undefined} data-id="element-70" data-mp="h85s103gccva" />
        </> : <>
          <main className="mx-auto max-w-[920px] px-5 pb-16 pt-8 md:px-8 md:pt-12" data-id="element-71" data-mp="3kow5u159zu7j">{children}</main>
          <footer className="mx-auto max-w-[920px] px-5 pb-10 text-xs text-fg-faint md:px-8" data-id="element-72" data-mp="3pnzctr4tgai5">
            Designed with love <span aria-label="love" role="img" data-id="element-73" data-mp="2czm0d4cwe1y">♥️</span> by Kat Diaz - 2026
          </footer>
        </>}
    </div>;
}